import React, { useState, useEffect, useMemo } from 'react';
import { livpureMockData } from '../../data';

const CategoryStatsTable = ({ selectedRegion, selectedCity, dateRange, showLocations, showProducts, showApps }) => {
  // View mode tabs (category, region, source)
  const [viewMode, setViewMode] = useState('category');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedRegion, selectedCity, dateRange, showLocations, showProducts, showApps]);

  // Set source type based on filters
  const sourceType = useMemo(() => {
    if (showLocations && !showProducts && !showApps) {
      return 'location';
    } else if (!showLocations && showProducts && !showApps) {
      return 'product';
    } else if (!showLocations && !showProducts && showApps) {
      return 'app';
    } else {
      return 'all';
    }
  }, [showLocations, showProducts, showApps]);
  
  // Filter reviews based on selected criteria
  const filteredReviews = useMemo(() => {
    let reviews = [...livpureMockData.reviews];
    
    // Filter by date range
    if (dateRange[0] && dateRange[1]) {
      const startDate = dateRange[0].getTime();
      const endDate = dateRange[1].getTime();
      reviews = reviews.filter(review => {
        const reviewDate = new Date(review.date).getTime();
        return reviewDate >= startDate && reviewDate <= endDate;
      });
    }
    
    // Filter by source type
    if (sourceType === 'location') {
      reviews = reviews.filter(review => review.source === 'Google Reviews');
    } else if (sourceType === 'product') {
      reviews = reviews.filter(review => review.source === 'Amazon');
    } else if (sourceType === 'app') {
      reviews = reviews.filter(review => 
        review.source === 'App Store' || review.source === 'Play Store'
      );
    }
    
    // Filter by region and city
    if (selectedRegion !== 'global') {
      reviews = reviews.filter(review => review.region === selectedRegion);
      
      if (selectedCity) {
        reviews = reviews.filter(review => review.city === selectedCity);
      }
    }
    
    return reviews;
  }, [dateRange, sourceType, selectedRegion, selectedCity]);
  
  // Extract and analyze by category
  const categoryData = useMemo(() => {
    const result = {};
    
    filteredReviews.forEach(review => {
      review.categorySentiments.forEach(cs => {
        // Initialize category if not exists
        if (!result[cs.category]) {
          result[cs.category] = {
            name: cs.category,
            total: 0,
            positive: 0,
            negative: 0,
            neutral: 0,
            sources: {},
            regions: {}
          };
        }
        
        // Count by sentiment
        result[cs.category].total++;
        if (cs.sentiment === 'Positive') {
          result[cs.category].positive++;
        } else if (cs.sentiment === 'Negative') {
          result[cs.category].negative++;
        } else {
          result[cs.category].neutral++;
        }
        
        // Count by source
        if (!result[cs.category].sources[review.source]) {
          result[cs.category].sources[review.source] = {
            total: 0,
            positive: 0,
            negative: 0,
            neutral: 0
          };
        }
        result[cs.category].sources[review.source].total++;
        if (cs.sentiment === 'Positive') {
          result[cs.category].sources[review.source].positive++;
        } else if (cs.sentiment === 'Negative') {
          result[cs.category].sources[review.source].negative++;
        } else {
          result[cs.category].sources[review.source].neutral++;
        }
        
        // Count by region
        if (!result[cs.category].regions[review.region]) {
          result[cs.category].regions[review.region] = {
            total: 0,
            positive: 0,
            negative: 0,
            neutral: 0,
            cities: {}
          };
        }
        result[cs.category].regions[review.region].total++;
        if (cs.sentiment === 'Positive') {
          result[cs.category].regions[review.region].positive++;
        } else if (cs.sentiment === 'Negative') {
          result[cs.category].regions[review.region].negative++;
        } else {
          result[cs.category].regions[review.region].neutral++;
        }
        
        // Count by city
        if (!result[cs.category].regions[review.region].cities[review.city]) {
          result[cs.category].regions[review.region].cities[review.city] = {
            total: 0,
            positive: 0,
            negative: 0,
            neutral: 0
          };
        }
        result[cs.category].regions[review.region].cities[review.city].total++;
        if (cs.sentiment === 'Positive') {
          result[cs.category].regions[review.region].cities[review.city].positive++;
        } else if (cs.sentiment === 'Negative') {
          result[cs.category].regions[review.region].cities[review.city].negative++;
        } else {
          result[cs.category].regions[review.region].cities[review.city].neutral++;
        }
      });
    });
    
    // Convert to array and sort by total mentions
    return Object.values(result).sort((a, b) => b.total - a.total);
  }, [filteredReviews]);
  
  // Extract and analyze by region
  const regionData = useMemo(() => {
    const result = {};
    const mainRegions = {};
    
    // Get main regions
    filteredReviews.forEach(review => {
      const region = review.region;
      const mainRegion = region.match(/\d+$/) ? region.replace(/\s+\d+$/, '') : region;
      
      // Add to main regions mapping
      if (!mainRegions[mainRegion]) {
        mainRegions[mainRegion] = new Set();
      }
      mainRegions[mainRegion].add(region);
      
      // Initialize region if not exists
      if (!result[region]) {
        result[region] = {
          name: region,
          mainRegion,
          total: 0,
          positive: 0,
          negative: 0,
          neutral: 0,
          categories: {},
          cities: {}
        };
      }
      
      // Count by sentiment
      result[region].total++;
      if (review.sentiment === 'Positive') {
        result[region].positive++;
      } else if (review.sentiment === 'Negative') {
        result[region].negative++;
      } else {
        result[region].neutral++;
      }
      
      // Count by category
      review.categorySentiments.forEach(cs => {
        if (!result[region].categories[cs.category]) {
          result[region].categories[cs.category] = {
            total: 0,
            positive: 0,
            negative: 0,
            neutral: 0
          };
        }
        result[region].categories[cs.category].total++;
        if (cs.sentiment === 'Positive') {
          result[region].categories[cs.category].positive++;
        } else if (cs.sentiment === 'Negative') {
          result[region].categories[cs.category].negative++;
        } else {
          result[region].categories[cs.category].neutral++;
        }
      });
      
      // Count by city
      if (!result[region].cities[review.city]) {
        result[region].cities[review.city] = {
          total: 0,
          positive: 0,
          negative: 0,
          neutral: 0,
          categories: {}
        };
      }
      result[region].cities[review.city].total++;
      if (review.sentiment === 'Positive') {
        result[region].cities[review.city].positive++;
      } else if (review.sentiment === 'Negative') {
        result[region].cities[review.city].negative++;
      } else {
        result[region].cities[review.city].neutral++;
      }
      
      // Count by category in city
      review.categorySentiments.forEach(cs => {
        if (!result[region].cities[review.city].categories[cs.category]) {
          result[region].cities[review.city].categories[cs.category] = {
            total: 0,
            positive: 0,
            negative: 0,
            neutral: 0
          };
        }
        result[region].cities[review.city].categories[cs.category].total++;
        if (cs.sentiment === 'Positive') {
          result[region].cities[review.city].categories[cs.category].positive++;
        } else if (cs.sentiment === 'Negative') {
          result[region].cities[review.city].categories[cs.category].negative++;
        } else {
          result[region].cities[review.city].categories[cs.category].neutral++;
        }
      });
    });
    
    // Add main region stats
    Object.entries(mainRegions).forEach(([mainRegion, subRegions]) => {
      if (!result[mainRegion]) {
        const subRegionList = Array.from(subRegions);
        const total = subRegionList.reduce((sum, r) => sum + (result[r]?.total || 0), 0);
        const positive = subRegionList.reduce((sum, r) => sum + (result[r]?.positive || 0), 0);
        const negative = subRegionList.reduce((sum, r) => sum + (result[r]?.negative || 0), 0);
        const neutral = subRegionList.reduce((sum, r) => sum + (result[r]?.neutral || 0), 0);
        
        result[mainRegion] = {
          name: mainRegion,
          mainRegion: mainRegion, // Same as name
          isMainRegion: true,
          total,
          positive,
          negative,
          neutral,
          categories: {},
          subRegions: subRegionList
        };
        
        // Aggregate categories from subregions
        const categories = {};
        subRegionList.forEach(subRegion => {
          Object.entries(result[subRegion].categories).forEach(([category, stats]) => {
            if (!categories[category]) {
              categories[category] = { total: 0, positive: 0, negative: 0, neutral: 0 };
            }
            categories[category].total += stats.total;
            categories[category].positive += stats.positive;
            categories[category].negative += stats.negative;
            categories[category].neutral += stats.neutral;
          });
        });
        result[mainRegion].categories = categories;
      }
    });
    
    // Convert to array and sort by total mentions
    return Object.values(result).sort((a, b) => {
      // First by main region status
      if (a.isMainRegion && !b.isMainRegion) return -1;
      if (!a.isMainRegion && b.isMainRegion) return 1;
      
      // Then by main region name
      if (a.mainRegion !== b.mainRegion) return a.mainRegion.localeCompare(b.mainRegion);
      
      // Then by total
      return b.total - a.total;
    });
  }, [filteredReviews]);
  
  // Extract and analyze by source
  const sourceData = useMemo(() => {
    const result = {};
    
    // Group by source type
    filteredReviews.forEach(review => {
      const source = review.source;
      
      // Initialize source if not exists
      if (!result[source]) {
        result[source] = {
          name: source,
          type: source === 'Google Reviews' ? 'location' : 
                source === 'Amazon' ? 'product' : 'app',
          total: 0,
          positive: 0,
          negative: 0,
          neutral: 0,
          categories: {}
        };
      }
      
      // Count by sentiment
      result[source].total++;
      if (review.sentiment === 'Positive') {
        result[source].positive++;
      } else if (review.sentiment === 'Negative') {
        result[source].negative++;
      } else {
        result[source].neutral++;
      }
      
      // Count by category
      review.categorySentiments.forEach(cs => {
        if (!result[source].categories[cs.category]) {
          result[source].categories[cs.category] = {
            total: 0,
            positive: 0,
            negative: 0,
            neutral: 0
          };
        }
        result[source].categories[cs.category].total++;
        if (cs.sentiment === 'Positive') {
          result[source].categories[cs.category].positive++;
        } else if (cs.sentiment === 'Negative') {
          result[source].categories[cs.category].negative++;
        } else {
          result[source].categories[cs.category].neutral++;
        }
      });
    });
    
    // Convert to array and sort by total mentions
    return Object.values(result).sort((a, b) => b.total - a.total);
  }, [filteredReviews]);
  
  // Get data based on current view mode
  const currentData = useMemo(() => {
    switch (viewMode) {
      case 'category': return categoryData;
      case 'region': return regionData;
      case 'source': return sourceData;
      default: return categoryData;
    }
  }, [viewMode, categoryData, regionData, sourceData]);
  
  // Pagination
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return currentData.slice(startIndex, startIndex + itemsPerPage);
  }, [currentData, currentPage, itemsPerPage]);
  
  const totalPages = Math.ceil(currentData.length / itemsPerPage);
  
  // Calculate percentage
  const getPercentage = (part, total) => {
    return total > 0 ? Math.round((part / total) * 100) : 0;
  };
  
  // Get color class for sentiment percentage
  const getColorClass = (percentage) => {
    if (percentage >= 70) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };
  
  // Get background color class
  const getBgColorClass = (percentage) => {
    if (percentage >= 70) return 'bg-green-50';
    if (percentage >= 50) return 'bg-yellow-50';
    return 'bg-red-50';
  };
  
  // Get detailed information for the selected category
  const selectedCategoryDetails = useMemo(() => {
    if (!selectedCategory) return null;
    
    const details = categoryData.find(cat => cat.name === selectedCategory);
    if (!details) return null;
    
    // Get top 5 regions and sources
    const topRegions = Object.entries(details.regions)
      .map(([name, stats]) => ({
        name,
        ...stats
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
      
    const topSources = Object.entries(details.sources)
      .map(([name, stats]) => ({
        name,
        ...stats
      }))
      .sort((a, b) => b.total - a.total);
    
    return {
      details,
      topRegions,
      topSources
    };
  }, [selectedCategory, categoryData]);
  
  return (
    <div>
      {/* View mode tabs */}
      <div className="flex mb-4">
        <button
          className={`mr-4 px-3 py-1 text-sm font-medium rounded-md ${
            viewMode === 'category'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          onClick={() => setViewMode('category')}
        >
          By Category
        </button>
        <button
          className={`mr-4 px-3 py-1 text-sm font-medium rounded-md ${
            viewMode === 'region'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          onClick={() => {setViewMode('region'); setSelectedCategory(null)}}
        >
          By Region
        </button>
        <button
          className={`px-3 py-1 text-sm font-medium rounded-md ${
            viewMode === 'source'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          onClick={() => {setViewMode('source'); setSelectedCategory(null)}}
        >
          By Source
        </button>
      </div>
      <div className='flex'>
      <div className={`overflow-x-auto ${selectedCategory ? "w-1/3" : "w-full"}`}>
        <table className={`min-w-full divide-y divide-gray-200`}>
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {viewMode === 'category' ? 'Category' : viewMode === 'region' ? 'Region' : 'Source'}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Mentions
              </th>
              {selectedCategory===null && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Positive
              </th>}
              {selectedCategory===null &&<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Negative
              </th>}
              {selectedCategory===null &&<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Neutral
              </th>}
              {selectedCategory===null &&<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sentiment
              </th>}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedData.map((item, index) => (
              <tr 
                key={index} 
                className={`hover:bg-gray-50 ${
                  viewMode === 'category' && selectedCategory === item.name ? 'bg-blue-50' : ''
                } ${item.isMainRegion ? 'bg-gray-50 font-medium' : ''}`}
                onClick={() => {
                  if (viewMode === 'category') {
                    setSelectedCategory(selectedCategory === item.name ? null : item.name);
                  }
                }}
                style={{ cursor: viewMode === 'category' ? 'pointer' : 'default' }}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900 flex items-center">
                    {item.isMainRegion && (
                      <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    )}
                    {item.name}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">{item.total}</div>
                </td>
                {selectedCategory===null &&<td className="px-6 py-4 whitespace-nowrap">
                  <div className={`text-sm ${getColorClass(getPercentage(item.positive, item.total))}`}>
                    {item.positive} ({getPercentage(item.positive, item.total)}%)
                  </div>
                </td>}
                {selectedCategory===null &&<td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">
                    {item.negative} ({getPercentage(item.negative, item.total)}%)
                  </div>
                </td>}
                {selectedCategory===null &&<td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">
                    {item.neutral} ({getPercentage(item.neutral, item.total)}%)
                  </div>
                </td>}
                {selectedCategory===null &&<td className="px-6 py-4 whitespace-nowrap">
                  <div className="w-32 bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-gradient-to-r from-red-500 via-yellow-300 to-green-500 h-2.5 rounded-full" 
                      style={{ width: `${getPercentage(item.positive, item.total)}%` }}
                    ></div>
                  </div>
                </td>}
              </tr>
            ))}
            
            {paginatedData.length === 0 && (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                  No data available with current filters
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
         {/* Selected Category Details */}
      {selectedCategoryDetails && (
        <div className='w-2/3 bg-gray-50'>
          <h5 className="text-md text-center font-medium text-gray-900 my-3">
            {selectedCategory} Details
          </h5>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 px-4">
            {/* Top Regions */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Top Regions</h4>
              <div className="space-y-4">
                {selectedCategoryDetails.topRegions.map((region, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">{region.name}</span>
                      <span className={`text-sm ${getColorClass(getPercentage(region.positive, region.total))}`}>
                        {getPercentage(region.positive, region.total)}% positive
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            getPercentage(region.positive, region.total) >= 70 ? 'bg-green-500' :
                            getPercentage(region.positive, region.total) >= 50 ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${getPercentage(region.positive, region.total)}%` }}
                        ></div>
                      </div>
                      <span className="ml-2 text-xs text-gray-500 min-w-[40px] text-right">
                        {region.total}
                      </span>
                    </div>
                    <div className="flex text-xs mt-1 text-gray-500">
                      <span className="mr-4">Positive: {region.positive}</span>
                      <span className="mr-4">Negative: {region.negative}</span>
                      <span>Neutral: {region.neutral}</span>
                    </div>
                  </div>
                ))}
                
                {selectedCategoryDetails.topRegions.length === 0 && (
                  <div className="text-sm text-gray-500">
                    No region data available
                  </div>
                )}
              </div>
            </div>
            
            {/* Top Sources */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="text-sm font-medium text-gray-700 mb-3">By Source</h4>
              <div className="space-y-4">
                {selectedCategoryDetails.topSources.map((source, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">{source.name}</span>
                      <span className={`text-sm ${getColorClass(getPercentage(source.positive, source.total))}`}>
                        {getPercentage(source.positive, source.total)}% positive
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            getPercentage(source.positive, source.total) >= 70 ? 'bg-green-500' :
                            getPercentage(source.positive, source.total) >= 50 ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${getPercentage(source.positive, source.total)}%` }}
                        ></div>
                      </div>
                      <span className="ml-2 text-xs text-gray-500 min-w-[40px] text-right">
                        {source.total}
                      </span>
                    </div>
                    <div className="flex text-xs mt-1 text-gray-500">
                      <span className="mr-4">Positive: {source.positive}</span>
                      <span className="mr-4">Negative: {source.negative}</span>
                      <span>Neutral: {source.neutral}</span>
                    </div>
                  </div>
                ))}
                
                {selectedCategoryDetails.topSources.length === 0 && (
                  <div className="text-sm text-gray-500">
                    No source data available
                  </div>
                )}
              </div>
            </div>
            
            {/* Sentiment Breakdown */}
            <div className="bg-white p-4 mb-4 rounded-lg shadow-sm col-span-1 md:col-span-2">
              <h4 className="text-sm font-medium text-gray-700 mb-2 text-center">Sentiment Distribution</h4>
              <div className="flex items-center justify-center space-x-8">
                <div className="text-center">
                  <div className="text-xl font-bold text-green-600">
                    {getPercentage(selectedCategoryDetails.details.positive, selectedCategoryDetails.details.total)}%
                  </div>
                  <div className="text-sm text-gray-500">Positive</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-red-600">
                    {getPercentage(selectedCategoryDetails.details.negative, selectedCategoryDetails.details.total)}%
                  </div>
                  <div className="text-sm text-gray-500">Negative</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-gray-500">
                    {getPercentage(selectedCategoryDetails.details.neutral, selectedCategoryDetails.details.total)}%
                  </div>
                  <div className="text-sm text-gray-500">Neutral</div>
                </div>
              </div>
              <div className="mt-4 w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                <div className="flex h-full">
                  <div 
                    className="h-full bg-green-500" 
                    style={{ width: `${getPercentage(selectedCategoryDetails.details.positive, selectedCategoryDetails.details.total)}%` }}
                  ></div>
                  <div 
                    className="h-full bg-red-500" 
                    style={{ width: `${getPercentage(selectedCategoryDetails.details.negative, selectedCategoryDetails.details.total)}%` }}
                  ></div>
                  <div 
                    className="h-full bg-gray-400" 
                    style={{ width: `${getPercentage(selectedCategoryDetails.details.neutral, selectedCategoryDetails.details.total)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-4">
          <div className="text-sm text-gray-500">
            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, currentData.length)} of {currentData.length} entries
          </div>
          <div className="flex">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
              // Logic to show pages around current page
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={i}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === pageNum
                      ? 'bg-blue-50 text-blue-600 z-10'
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
      
     
    </div>
  );
};

export default CategoryStatsTable;